import {
    EntryStatus,
    Nullable,
    OutwardReferralData,
    RecommendationData,
    ReferralData,
    ReferralStatus,
    ReservationData,
    TaskData,
    TaskStatus,
} from 'chaika-frontend-types/frontend-cms';
import { Currency } from 'chaika-enums';

import { DateFormat, DateFormatItem, DateHelper } from '@utils/DateHelper';
import { Drug } from '@store/models/SystemFields/Drug';
import { GroupedListParams } from '@store/abstract/GroupedList';
import { EntryListParams } from '@patient/store/ehr/models/EntryList';

import { ItemParams, ItemType, ReferralItemParams } from '@common/TreatmentPlan/types';
import { translate } from '@chaika/i18n/helper';

interface ItemDateParams {
    startDate?: Nullable<string>;
    endDate?: Nullable<string>;
    exactDate?: boolean;
}

interface ItemParamsWithOrder extends ItemParams {
    orderByDate: string;
}

const TYPES_WITH_PRICE = new Set([ItemType.Reservation, ItemType.Referral]);
const TYPES_WITH_COST = new Set([ItemType.OutwardReferral]);

export function formatItemDate({ startDate, endDate, exactDate }: ItemDateParams): string {
    if (exactDate && startDate) {
        return DateHelper.format(startDate, DateFormat.DateWithTime);
    }

    if (!startDate) {
        return translate('indefinitely');
    }

    const from = translate('fromSecond');

    if (!endDate) {
        return `${from} ${DateHelper.formatDateWithYearAwareness(startDate)}`;
    }

    if (DateHelper.isSameDay(startDate, endDate)) {
        return DateHelper.formatDateWithYearAwareness(startDate);
    }

    const to = translate('to').toLowerCase();

    return `${from} ${formatRangeStart(startDate, endDate)} ${to} ${formatRangeEnd(startDate, endDate)}`;
}

export function isItemTypeWithPrice(type: ItemType): boolean {
    return TYPES_WITH_PRICE.has(type);
}

export function isItemTypeWithCost(type: ItemType): boolean {
    return TYPES_WITH_COST.has(type);
}

export function reservationToItemParams(reservation: ReservationData): ItemParamsWithOrder {
    const { id, startTime: startDate } = reservation;

    return {
        id,
        startDate,
        isExpired: isExpiredItem(startDate),
        orderByDate: startDate,
        type: ItemType.Reservation,
    };
}

export function taskToItemParams(task: TaskData): ItemParamsWithOrder {
    const { id, start: startDate, end: endDate, status } = task;

    return {
        id,
        startDate,
        endDate,
        isExecuted: status === TaskStatus.Completed,
        isExpired: isExpiredItem(endDate),
        orderByDate: getOrderByDate(startDate, endDate),
        type: ItemType.Task,
    };
}

export function recommendationToItemParams(
    recommendation: RecommendationData,
): ItemParamsWithOrder & { isImportant: boolean } {
    const { id, start: startDate, end: endDate, isImportant = false } = recommendation;

    return {
        id,
        startDate,
        endDate,
        isImportant,
        isExpired: isExpiredItem(endDate),
        orderByDate: getOrderByDate(startDate, endDate),
        type: ItemType.Recommendation,
    };
}

export function drugToItemParams(drug: Drug, entryDate?: string): ItemParamsWithOrder {
    return {
        id: drug.id,
        startDate: drug.start,
        endDate: drug.end,
        isExpired: isExpiredItem(drug.end, entryDate),
        orderByDate: !!drug.start ? getOrderByDate(drug.start, drug.end) : DateHelper.endOfMonth().toISOString(),
        type: ItemType.Drug,
    };
}

export function outwardReferralToItemParams(
    outwardReferral: OutwardReferralData,
): ItemParamsWithOrder & { costFrom: number; currency?: Currency } {
    const { id, startDate, duration, costFrom, currency } = outwardReferral;
    const endDate = DateHelper.addOptionalDays(startDate, duration)?.toISOString();

    return {
        id,
        startDate,
        endDate,
        currency,
        costFrom: costFrom ?? 0,
        isExpired: isExpiredItem(endDate),
        orderByDate: getOrderByDate(startDate, endDate),
        type: ItemType.OutwardReferral,
    };
}

export function referralToItemParams(
    referral: GroupedListParams<ReferralData>,
    fromEhr?: boolean,
): ItemParamsWithOrder {
    const { groupId: id, startDate, duration, count, status } = referral;
    const endDate = DateHelper.addOptionalDays(startDate, duration)?.toISOString();

    return {
        id,
        count,
        startDate,
        endDate,
        isExpired: isExpiredItem(endDate),
        isExecuted: status === ReferralStatus.Executed,
        orderByDate: getOrderByDate(startDate, endDate),
        type: ItemType.Referral,
    };
}

export function referralToFullItemParams(referral: GroupedListParams<ReferralData>): ReferralItemParams {
    const { groupId: id, startDate, duration, count, id: referralId, serviceId } = referral;
    const endDate = DateHelper.addOptionalDays(startDate, duration)?.toISOString();

    return {
        id,
        referralId,
        count,
        startDate,
        endDate,
        serviceId,
        isExpired: isExpiredItem(endDate),
        type: ItemType.Referral,
    };
}

export function entryToItemParams(entry: EntryListParams): ItemParamsWithOrder {
    const { id, creationTime, status } = entry;

    return {
        id,
        startDate: creationTime,
        endDate: creationTime,
        isExpired: isExpiredItem(creationTime),
        orderByDate: creationTime,
        type: ItemType.Entry,
        hideOnPrint: status !== EntryStatus.Signed,
    };
}

function formatRangeStart(startDate: string, endDate: string): string {
    const isFromCurrentYear = DateHelper.isSameYear(startDate, new Date());

    if (!isFromCurrentYear) {
        return DateHelper.format(startDate, DateFormat.DateWithMonthString);
    }

    return DateHelper.isSameMonth(startDate, endDate)
        ? DateHelper.format(startDate, DateFormatItem.Day)
        : DateHelper.format(startDate, DateFormat.DayWithMonthString);
}

function formatRangeEnd(startDate: string, endDate: string): string {
    const currentDate = new Date();
    const areBothFromCurrentYear =
        DateHelper.isSameYear(endDate, currentDate) && DateHelper.isSameYear(startDate, currentDate);

    return areBothFromCurrentYear
        ? DateHelper.format(endDate, DateFormat.DayWithMonthString)
        : DateHelper.format(endDate, DateFormat.DateWithMonthString);
}

function getOrderByDate(startDate?: string, endDate?: Nullable<string>): string {
    const now = DateHelper.now();

    const isEndDateCloser =
        !!endDate &&
        !!startDate &&
        ((DateHelper.isBefore(startDate, now) && DateHelper.isAfter(endDate, now)) ||
            DateHelper.isBefore(endDate, now));

    return (isEndDateCloser ? endDate : startDate) ?? new Date().toISOString();
}

function isExpiredItem(expireDate?: Nullable<string>, comparedDate?: Nullable<string>): boolean {
    return !!expireDate && DateHelper.isBefore(expireDate, DateHelper.getStartOfDay(comparedDate ?? Date.now()));
}
